import { useTranslation } from "react-i18next";
import { getLongPeriodFormat } from "../../../../common/util/period";
import { storedLocale } from "../../../Authentication/util";
import { IconCarrot } from "../../../../common/icons/IconCarrot/IconCarrot";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import defaultContent from "../../../../content/ssiUplineReport";
import "../../../../common/scss/table.scss";
import "../../Shared/SsiUplineTable.scss";
import "./EarnedPaymentTable.scss";
import { SsiEligibilityWindowExtended } from "../../../../services/SSIUpline/ssiUplineAPI.types";

export type EarnedPaymentRow = {
  aff: string;
  aboName: string;
  aboNumber: string;
  programStartDate: string;
  paymentMonth: string;
  sponsorName: string;
  sponsorNumber: string;
  paymentCount?: number; // Number of payments received (for grouping)
  isHighlighted?: boolean;
  scrollRef?: boolean;
  eligibilityWindow: SsiEligibilityWindowExtended;
};

export type GroupedEarnedPaymentRow =
  | { type: "group"; paymentCount: number }
  | { type: "data"; data: EarnedPaymentRow };

type Props = {
  rows: GroupedEarnedPaymentRow[];
  isLoading: boolean;
  onProfileClick?: (
    aboNumber: string,
    aff: string,
    sponsorNumber: string,
    eligibilityWindow: SsiEligibilityWindowExtended,
  ) => void;
};

const EarnedPaymentTable = ({ rows, isLoading, onProfileClick }: Props) => {
  const { t } = useTranslation(["ssiUplineReport"]);
  const locale = storedLocale();

  const formatDate = (period: string) => {
    if (!period) return "";
    return getLongPeriodFormat(period, locale);
  };

  return (
    <div className="EarnedPaymentTable">
      <div className="SSIUplineTable">
        {isLoading ? (
          <table className="Table Table--rounded Table--noFirstChildPadding">
            <thead>
              <tr>
                <th>
                  <LeafRectangleLoader width="10rem" height="1rem" margin="0" isLoading={true} />
                </th>
                <th>
                  <LeafRectangleLoader width="10rem" height="1rem" margin="0" isLoading={true} />
                </th>
                <th>
                  <LeafRectangleLoader width="8rem" height="1rem" margin="0" isLoading={true} />
                </th>
                <th>
                  <LeafRectangleLoader width="6rem" height="1rem" margin="0" isLoading={true} />
                </th>
              </tr>
            </thead>
            <tbody className="TableBody TableBody--oneRemPaddingForFirstChildThenNoLeftPadding TableBody--rowBorder">
              <tr className="row">
                <td>
                  <div className="aboNameNum">
                    <LeafRectangleLoader width="8rem" height="1rem" margin="0 0 0.25rem 0" isLoading={true} />
                    <LeafRectangleLoader width="4rem" height="0.75rem" margin="0" isLoading={true} />
                  </div>
                </td>
                <td>
                  <LeafRectangleLoader width="8rem" height="1rem" margin="0" isLoading={true} />
                </td>
                <td>
                  <LeafRectangleLoader width="8rem" height="1rem" margin="0" isLoading={true} />
                </td>
                <td>
                  <div className="sponsorNameCell">
                    <LeafRectangleLoader width="6rem" height="1rem" margin="0" isLoading={true} />
                    <LeafRectangleLoader width="6rem" height="1rem" margin="0" isLoading={true} />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <table className="Table Table--rounded Table--noFirstChildPadding">
            <thead>
              <tr>
                <th>
                  {t("earnedPayment.columns.aboNameNum", defaultContent.earnedPayment.columns.aboNameNum).toString()}
                </th>
                <th>
                  {t(
                    "earnedPayment.columns.programStartDate",
                    defaultContent.earnedPayment.columns.programStartDate,
                  ).toString()}
                </th>
                <th>
                  {t(
                    "earnedPayment.columns.paymentMonth",
                    defaultContent.earnedPayment.columns.paymentMonth,
                  ).toString()}
                </th>
                <th>{t("earnedPayment.columns.sponsor", defaultContent.earnedPayment.columns.sponsor).toString()}</th>
              </tr>
            </thead>
            <tbody className="TableBody TableBody--oneRemPaddingForFirstChildThenNoLeftPadding TableBody--rowBorder">
              {rows.map((row, ind) => {
                if (row.type === "group") {
                  return (
                    <tr key={`group-${ind}`} className="groupRow">
                      <td colSpan={4} className="groupCell">
                        {row.paymentCount}{" "}
                        {row.paymentCount === 1
                          ? t("earnedPayment.paymentReceived", defaultContent.earnedPayment.paymentReceived).toString()
                          : t(
                              "earnedPayment.paymentsReceived",
                              defaultContent.earnedPayment.paymentsReceived,
                            ).toString()}
                      </td>
                    </tr>
                  );
                }

                const dataRow = row.data;
                return (
                  <tr
                    key={ind}
                    className={`row ${dataRow.isHighlighted ? "row__highlighted" : ""}`}
                    aria-selected={dataRow.isHighlighted}
                  >
                    <td
                      aria-label={dataRow.aboName}
                      className={`${dataRow.isHighlighted ? "highlightLeftmostCell" : ""} ${
                        ind === rows.length - 1 ? "lastRowBorderLeft" : ""
                      }`}
                    >
                      <div className="aboNameNum">
                        <span className="name">{dataRow.aboName}</span>
                        <span className="num">#{dataRow.aboNumber}</span>
                      </div>
                    </td>
                    <td>{formatDate(dataRow.programStartDate)}</td>
                    <td>{formatDate(dataRow.paymentMonth)}</td>
                    <td
                      aria-label={dataRow.sponsorName}
                      className={`${dataRow.isHighlighted ? "highlightRightmostCell" : ""} ${
                        ind === rows.length - 1 ? "lastRowBorderRight" : ""
                      }`}
                    >
                      <div className="sponsorNameCell">
                        <span className="name">{dataRow.sponsorName}</span>
                        <button
                          type="button"
                          className="button"
                          id={`EarnedPaymentTableProfileButton${dataRow.aboNumber}`}
                          onClick={() => {
                            onProfileClick?.(
                              dataRow.aboNumber,
                              dataRow.aff,
                              dataRow.sponsorNumber,
                              dataRow.eligibilityWindow,
                            );
                          }}
                        >
                          <div className="buttonContents">
                            <div>
                              <span className="profileText">{t("profile", defaultContent.profile as string)}</span>
                              <div className="underline"></div>
                            </div>
                            <IconCarrot orientation="right" color="black" />
                          </div>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default EarnedPaymentTable;
